import { NextSeo } from "next-seo";
import { LandingHeader } from "@/components/landing/landing-header";

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Use the same floating header from landing page */}
      <NextSeo
        canonical="https://www.vacwork.app/privacy"
        description="Read VacWork's privacy policy to learn how we collect, use, and protect your personal information."
        title="Privacy Policy | VacWork"
      />
      <LandingHeader showSectionLinks={false} />

      {/* Main Content with top padding to account for fixed header */}
      <main className="container mx-auto max-w-4xl px-4 pt-24 pb-12">
        <h1 className="mb-8 font-light text-3xl md:text-4xl">Privacy Policy</h1>

        <div className="prose prose-neutral dark:prose-invert max-w-none">
          <p className="mb-6 text-muted-foreground">
            Effective Date: 17 June 2025
          </p>

          <section className="mb-10">
            <p className="mb-4">
              VacWork ("we", "us", or "our") is committed to protecting your
              privacy. This Privacy Policy explains how we collect, use,
              disclose, and safeguard your personal information when you visit
              or use our website and services ("Platform"). By using VacWork,
              you agree to the terms of this Privacy Policy.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="mb-4 font-light text-xl md:text-2xl">
              1. Information We Collect
            </h2>
            <p className="mb-4">
              We may collect and process the following categories of personal
              information:
            </p>

            <p className="mb-2">
              <strong>Identity Data:</strong> Name, ID number, date of birth.
            </p>
            <p className="mb-2">
              <strong>Contact Data:</strong> Email address, phone number,
              physical address.
            </p>
            <p className="mb-2">
              <strong>Employment Data:</strong> CVs, job applications, work
              history, qualifications.
            </p>
            <p className="mb-2">
              <strong>Usage Data:</strong> IP address, browser type, access
              times, referring URLs, and activity logs.
            </p>
            <p className="mb-2">
              <strong>Device Data:</strong> Device identifiers, operating
              system, device type.
            </p>
            <p className="mb-4">
              <strong>Cookies and Tracking Data:</strong> Session data and usage
              analytics (see Section 7).
            </p>

            <p className="mb-2">We collect this information:</p>
            <ul className="mb-4 list-disc pl-6">
              <li className="mb-2">
                When you create an account or complete your profile.
              </li>
              <li className="mb-2">When you apply for or post a job.</li>
              <li>Through automated tracking technologies.</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="mb-4 font-light text-xl md:text-2xl">
              2. How We Use Your Information
            </h2>
            <p className="mb-4">We use your information to:</p>
            <ul className="mb-4 list-disc pl-6">
              <li className="mb-2">
                Provide, maintain, and improve our platform and services.
              </li>
              <li className="mb-2">
                Match candidates with suitable job opportunities.
              </li>
              <li className="mb-2">
                Communicate with you regarding your account, jobs, or platform
                updates.
              </li>
              <li className="mb-2">
                Comply with legal obligations and respond to law enforcement
                requests.
              </li>
              <li>Ensure the security and integrity of our systems.</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="mb-4 font-light text-xl md:text-2xl">
              3. Legal Basis for Processing (POPIA/GDPR)
            </h2>
            <p className="mb-4">
              Where applicable, we process your personal data based on:
            </p>
            <ul className="mb-4 list-disc pl-6">
              <li className="mb-2">
                Your consent (e.g. when you opt into communications).
              </li>
              <li className="mb-2">
                Contractual necessity (e.g. to process job applications).
              </li>
              <li className="mb-2">
                Legal obligations (e.g. data retention laws).
              </li>
              <li>
                Our legitimate interest (e.g. platform improvement, fraud
                prevention).
              </li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="mb-4 font-light text-xl md:text-2xl">
              4. Sharing of Information
            </h2>
            <p className="mb-4">We do not sell your personal data.</p>
            <p className="mb-4">We may share your information with:</p>
            <ul className="mb-4 list-disc pl-6">
              <li className="mb-2">
                Verified employers and recruiters you apply to or express
                interest in.
              </li>
              <li className="mb-2">
                Service providers who support platform operations (e.g.
                analytics, hosting).
              </li>
              <li className="mb-2">
                Law enforcement or government entities when legally required.
              </li>
            </ul>
            <p>
              All third-party processors are contractually obligated to protect
              your data.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="mb-4 font-light text-xl md:text-2xl">
              5. Data Retention
            </h2>
            <p className="mb-4">
              We retain your data only for as long as necessary:
            </p>
            <ul className="mb-4 list-disc pl-6">
              <li className="mb-2">
                To provide our services and comply with legal requirements.
              </li>
              <li>
                You may request deletion of your data at any time (see Section
                8).
              </li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="mb-4 font-light text-xl md:text-2xl">
              6. Data Security
            </h2>
            <p className="mb-4">
              We implement reasonable technical and organisational measures to
              secure your data, including:
            </p>
            <ul className="mb-4 list-disc pl-6">
              <li className="mb-2">Encrypted data transmission (SSL).</li>
              <li className="mb-2">
                Access controls and role-based permissions.
              </li>
              <li className="mb-2">Regular security audits.</li>
            </ul>
            <p>
              However, no system is completely immune to breaches. Use the
              platform at your own risk.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="mb-4 font-light text-xl md:text-2xl">
              7. Cookies and Tracking
            </h2>
            <p className="mb-4">We use cookies to:</p>
            <ul className="mb-4 list-disc pl-6">
              <li className="mb-2">Improve website functionality.</li>
              <li className="mb-2">Analyse site usage and trends.</li>
              <li className="mb-2">Personalise your experience.</li>
            </ul>
            <p>
              You can manage your cookie preferences in your browser settings.
              Certain features may not function properly without cookies.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="mb-4 font-light text-xl md:text-2xl">
              8. Your Rights
            </h2>
            <p className="mb-4">You have the right to:</p>
            <ul className="mb-4 list-disc pl-6">
              <li className="mb-2">
                Access and correct your personal information.
              </li>
              <li className="mb-2">
                Request deletion or restriction of your data.
              </li>
              <li className="mb-2">Withdraw consent where applicable.</li>
              <li className="mb-2">
                Lodge a complaint with a data protection authority.
              </li>
            </ul>
            <p>
              To exercise these rights, please contact us at
              privacy@vacwork.co.za.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="mb-4 font-light text-xl md:text-2xl">
              9. International Data Transfers
            </h2>
            <p>
              If you access VacWork from outside South Africa, your data may be
              processed in countries with different data protection standards.
              We take steps to ensure adequate safeguards are in place.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="mb-4 font-light text-xl md:text-2xl">
              10. Children's Privacy
            </h2>
            <p>
              VacWork is not intended for use by anyone under 18. We do not
              knowingly collect data from minors.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="mb-4 font-light text-xl md:text-2xl">
              11. Updates to This Policy
            </h2>
            <p>
              We may update this Privacy Policy to reflect changes to our
              practices or legal obligations. The updated version will be posted
              on this page with a revised effective date. Continued use of our
              services constitutes your acceptance of the updated policy.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="mb-4 font-light text-xl md:text-2xl">
              12. Contact Us
            </h2>
            <p>
              For any questions or concerns about this Privacy Policy, please
              contact:
            </p>
            <p className="mt-2">
              <a href="mailto:admin@vacwork.app">📧 admin@vacwork.app</a>
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
